import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import {
  Edit,
  ArrowUpDown,
  Search,
  Plus,
  Calendar,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Factory,
  Globe,
  Phone,
  Mail,
  MapPin,
  User,
  Filter,
} from "lucide-react";
import { Manufacturer } from "../../../types/manufacturer";
import { manufacturerService } from "../../../services/manufacturerService";
import { LoadingSpinner } from "../../../shared/LoadingSpinner";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import ManufacturerForm from "./ManufacturerForm";

// Activate/Deactivate uses full-size right-side drawer (same pattern as ProjectTypeTable)
import useDebounce from "../../../hooks/useDebounce";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Select from "../../../shared/components/form/Select";
import { userService } from "../../../services/userService";

import { BRAND_COLORS } from "../../../shared/constants/brandColors";

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  manufacturer: Manufacturer;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ manufacturer, onRefresh }) => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const manufacturerId = manufacturer._id || manufacturer.id;
      if (!manufacturerId) {
        throw new Error("Manufacturer ID is required");
      }

      await manufacturerService.updateManufacturer(String(manufacturerId), {
        status: manufacturer.status === "active" ? "inactive" : "active"
      });

      toast.success(`Manufacturer ${manufacturer.status === "active" ? "deactivated" : "activated"} successfully`);
      setDrawerOpen(false);
      onRefresh();
    } catch (err: unknown) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle manufacturer status");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = manufacturer.status === "active";

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setDrawerOpen(true)}
        title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${isActive
          ? "bg-green-50 border-green-200 hover:bg-green-100"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${isActive
            ? "translate-x-2 bg-green-500"
            : "-translate-x-2 bg-gray-400"
            }`}
        />
      </button>

      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            {/* Full-size right drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 z-[101] h-full w-full max-w-md sm:max-w-lg bg-white border-l border-gray-200 shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border flex-shrink-0 ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                    {isActive ? (
                      <XCircleIcon size={24} className="text-red-500" />
                    ) : (
                      <CheckCircle size={24} className="text-green-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      {isActive ? "Deactivate Manufacturer" : "Activate Manufacturer"}
                    </h2>
                    <p className="text-sm text-gray-600 break-words">
                      {manufacturer.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200 shrink-0"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200 flex-shrink-0">
                    <AlertTriangle size={20} className="text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-700 mb-4 leading-relaxed">
                      Are you sure you want to{" "}
                      <span className="font-medium text-gray-900">
                        {isActive ? "deactivate" : "activate"}
                      </span>{" "}
                      <span className="font-medium break-words" style={{ color: BRAND_COLORS.primary }}>{manufacturer.name}</span>?
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {isActive
                          ? "This manufacturer will no longer be available for selection in equipment."
                          : "This manufacturer will become available for selection in equipment."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 shrink-0">
                <button
                  onClick={() => setDrawerOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleToggle}
                  disabled={isLoading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg transition-all duration-200 disabled:opacity-50 ${isActive
                    ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                    : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>
                      {isActive ? "Deactivate" : "Activate"}
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface Props {
  refreshTrigger: number;
  onRefresh: () => void;
}

const ITEMS_PER_PAGE = 6;
const DEBOUNCE_DELAY = 500;

export default function ManufacturerTable({ refreshTrigger, onRefresh }: Props) {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<Manufacturer | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [status, setStatus] = useState<string | null>("");
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  // Status filter options
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Fetch user names for Created By column
  useEffect(() => {
    const fetchUserNames = async () => {
      const uniqueUserIds = [...new Set(
        manufacturers
          .map(m => m.createdBy)
          .filter((id): id is string => !!id)
      )];

      const missingIds = uniqueUserIds.filter(id => !userNames[id]);
      if (missingIds.length === 0) return;

      const results = await Promise.all(
        missingIds.map(async (userId) => {
          try {
            const user = await userService.getUserById(userId);
            return { id: userId, name: user.name };
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            return { id: userId, name: 'Unknown User' };
          }
        })
      );

      const newNames: Record<string, string> = {};
      results.forEach(({ id, name }) => { newNames[id] = name; });
      setUserNames(prev => ({ ...prev, ...newNames }));
    };

    if (manufacturers.length > 0) {
      fetchUserNames();
    }
  }, [manufacturers]);

  const getUserName = (userId?: string) => {
    if (!userId) return 'System';
    return userNames[userId] || 'Loading...';
  };

  const fetchManufacturers = useCallback(async (abortController?: AbortController) => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: any = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: sortField,
        sortOrder,
      };

      // Only add filters if they have values
      if (debouncedSearchTerm) filters.search = debouncedSearchTerm;
      filters.status = (status && status !== "") ? status : "all";

      const res = await manufacturerService.getManufacturers(filters);

      setManufacturers(res.data || []);
      setTotalItems(res.meta?.totalDocs || 0);
    } catch (err: any) {
      if (err.name === 'CanceledError') {
        console.log('Request was canceled');
        return;
      }

      setError(err.response?.data?.message || "Failed to fetch manufacturers");
      console.error("API Error:", err);
      setManufacturers([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, status, page, sortField, sortOrder]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchManufacturers(abortController);

    return () => abortController.abort();
  }, [fetchManufacturers]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchManufacturers();
    }
  }, [refreshTrigger, fetchManufacturers]);

  const handleAdd = (item: Manufacturer) => {
    setEditingItem(null);
    setOpenForm(false);
    setPage(1);
    fetchManufacturers();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("");
      setSortOrder("asc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setSortMenuOpen(null);
    setPage(1);
  };

  // Ctrl+K to focus
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <>
      {error && (
        <ErrorAlert
          message={error}
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg border border-gray-200"
      >
        {/* Controls Section */}
        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left Side: Search */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search manufacturers... (Ctrl+K)"
                  className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                  style={{
                    '--tw-ring-color': BRAND_COLORS.primary,
                    'focusBorderColor': BRAND_COLORS.primary
                  } as React.CSSProperties}
                />
              </div>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${showFilters
                ? `text-white border-transparent`
                : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>

            {/* Right Side: Sort and Add */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {/* Sort Button */}
                <div className="relative">
                  <button
                    onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                    className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                    style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                  >
                    <ArrowUpDown size={16} />
                    Sort
                  </button>

                  {/* Sort Dropdown */}
                  <AnimatePresence>
                    {sortMenuOpen === "general" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full right-0 mt-2 z-40 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="p-3">
                          <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                          <button
                            onClick={() => handleSortOption("name", "asc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "asc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "name" && sortOrder === "asc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            A → Z
                          </button>
                          <button
                            onClick={() => handleSortOption("name", "desc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "desc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "name" && sortOrder === "desc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Z → A
                          </button>

                          <div className="border-t border-gray-200 my-2"></div>
                          <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                          <button
                            onClick={() => handleSortOption("createdAt", "desc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "desc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "createdAt" && sortOrder === "desc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Newest First
                          </button>
                          <button
                            onClick={() => handleSortOption("createdAt", "asc")}
                            className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "asc"
                              ? `font-medium bg-opacity-10`
                              : "text-gray-700"
                              }`}
                            style={sortField === "createdAt" && sortOrder === "asc" ? {
                              color: BRAND_COLORS.primary,
                              backgroundColor: BRAND_COLORS.primaryLight
                            } : {}}
                          >
                            Oldest First
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Add Manufacturer Button */}
              <button
                onClick={() => {
                  setEditingItem(null);
                  setOpenForm(true);
                }}
                className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                  '--tw-ring-color': BRAND_COLORS.primary
                } as React.CSSProperties}
              >
                <div className="relative">
                  <Plus size={16} className="absolute animate-ping opacity-40" />
                  <Plus size={16} className="relative" />
                </div>
                <span>Add Manufacturer</span>
              </button>
            </div>
          </div>
          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4"
              >
                <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  {/* Status Filter */}
                  <div className="flex-1 min-w-[180px]">
                    <Select
                      options={statusOptions}
                      value={statusOptions.find((opt) => opt.value === (status || ""))}
                      placeholder="Filter by Status"
                      onChange={(selectedOption) => {
                        setStatus(selectedOption?.value === "" ? null : selectedOption?.value || null);
                        setPage(1);
                      }}
                    />
                  </div>

                  {/* Reset Filters Button */}
                  {status && (
                    <button
                      type="button"
                      onClick={() => {
                        setStatus(null);
                        setPage(1);
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
                      style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                    >
                      <X size={14} />
                      Clear
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Table - Clean Design */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                {[
                  { label: "Code", field: "code", icon: <Factory size={14} /> },
                  { label: "Manufacturer Details", field: "name", icon: <Factory size={14} /> },
                  { label: "Contact Information", field: "contact", icon: <Mail size={14} /> },
                  { label: "Created By", field: "createdBy", icon: <User size={14} /> },
                ].map(({ label, field, icon }) => (
                  <th
                    key={field}
                    className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide"
                  >
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <AnimatePresence>
                {isLoading ? (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <td colSpan={5} className="p-16 text-center">
                      <LoadingSpinner />
                    </td>
                  </motion.tr>
                ) : manufacturers.length === 0 ? (
                  <motion.tr
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <td colSpan={5} className="p-16 text-center">
                      <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
                        style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
                      >
                        <Factory size={24} className="text-gray-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">No manufacturers found</h3>
                      <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                        {searchTerm
                          ? "Try adjusting your search criteria to find the manufacturers you're looking for"
                          : "Get started by adding your first manufacturer to the system"}
                      </p>
                      {!searchTerm && (
                        <button
                          onClick={() => {
                            setEditingItem(null);
                            setOpenForm(true);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
                          style={{
                            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                            '--tw-ring-color': BRAND_COLORS.primary
                          } as React.CSSProperties}
                        >
                          <Plus size={16} />
                          Add First Manufacturer
                        </button>
                      )}
                    </td>
                  </motion.tr>
                ) : (
                  manufacturers.map((manufacturer, index) => (
                    <motion.tr
                      key={manufacturer._id || manufacturer.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${manufacturer.status !== "active"
                        ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60"
                        : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                        }`}
                    >
                      {/* Code */}
                      <td className="p-4">
                        <div className="text-sm font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                          {manufacturer.code || 'Auto'}
                        </div>
                      </td>

                      {/* Manufacturer Details */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
                              <Factory size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium truncate ${manufacturer.status !== "active" ? "text-gray-500 line-through" : "text-gray-900"
                                }`}>
                                {manufacturer.name}
                              </span>
                            </div>
                            {manufacturer.description && (
                              <div className="text-xs text-gray-500 mt-1 truncate">
                                {manufacturer.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact Information */}
                      <td className="p-4">
                        <div className="text-sm text-gray-600 space-y-1">
                          {manufacturer.contactInfo?.email && (
                            <div className="flex items-center gap-2">
                              <Mail size={12} className={manufacturer.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                              <span className={`truncate max-w-40 ${manufacturer.status !== "active" ? "text-gray-500" : "text-gray-900"}`}
                                title={manufacturer.contactInfo.email}>
                                {manufacturer.contactInfo.email}
                              </span>
                            </div>
                          )}
                          {manufacturer.contactInfo?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone size={12} className={manufacturer.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                              <span className={`truncate ${manufacturer.status !== "active" ? "text-gray-500" : "text-gray-900"}`}>
                                {manufacturer.contactInfo.phone}
                              </span>
                            </div>
                          )}
                          {manufacturer.contactInfo?.website && (
                            <div className="flex items-center gap-2">
                              <Globe size={12} className={manufacturer.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                              <span className={`truncate max-w-32 ${manufacturer.status !== "active" ? "text-gray-500" : "text-gray-900"}`}
                                title={manufacturer.contactInfo.website}>
                                {manufacturer.contactInfo.website}
                              </span>
                            </div>
                          )}
                          {manufacturer.contactInfo?.address && (
                            <div className="flex items-center gap-2">
                              <MapPin size={12} className={manufacturer.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                              <span className={`truncate max-w-40 ${manufacturer.status !== "active" ? "text-gray-500" : "text-gray-900"}`}
                                title={manufacturer.contactInfo.address}>
                                {manufacturer.contactInfo.address}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className={manufacturer.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${manufacturer.status !== "active" ? "text-gray-500" : "text-gray-900"}`}>
                              {getUserName(manufacturer.createdBy)}
                            </span>
                          </div>
                          {manufacturer.createdAt && (
                            <>
                              <span className={`text-xs ${manufacturer.status !== "active" ? "text-gray-400" : "text-gray-500"}`}>
                                {new Date(manufacturer.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs ${manufacturer.status !== "active" ? "text-gray-400" : "text-gray-400"}`}>
                                {new Date(manufacturer.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-4">
                          <ToggleStatusCell manufacturer={manufacturer} onRefresh={onRefresh} />

                          <button
                            onClick={() => {
                              setEditingItem(manufacturer);
                              setOpenForm(true);
                            }}
                            title="Edit Manufacturer"
                            className={`transition-colors duration-200 ${manufacturer.status !== "active"
                              ? "text-gray-300 hover:text-gray-400"
                              : "text-gray-400 hover:text-emerald-400"
                              }`}
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Pagination - Clean Design */}
        {manufacturers.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm text-gray-600 font-medium">
                Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalItems)}</span> to{" "}
                <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * ITEMS_PER_PAGE, totalItems)}</span> of{" "}
                <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalItems}</span> manufacturers
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(page - 1, 1))}
                    disabled={page === 1}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${page === pageNum
                            ? "text-white border border-transparent"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                            }`}
                          style={page === pageNum ? {
                            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                          } : {}}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPage(Math.min(page + 1, totalPages))}
                    disabled={page === totalPages}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      <RightDrawer
        open={openForm}
        title={editingItem ? "Edit Manufacturer" : "Add Manufacturer"}
        onClose={() => {
          setOpenForm(false);
          setEditingItem(null);
        }}
        size="50"
      >
        <ManufacturerForm
          editingItem={editingItem}
          onAdd={handleAdd}
          onClose={() => {
            setOpenForm(false);
            setEditingItem(null);
          }}
        />
      </RightDrawer>
    </>
  );
}
